import { effectiveLanguage, runtime } from './runtime.js';
import { buildPageKey, normalizePagePath } from './page.js';
import { nudgeMaxTextLength, pageCoveragePollAttempts, pageCoveragePollMs, pageTextMaxLength } from './constants.js';
import { audience, pageMessageShownUrl, pageMessageUrl, pageSeenUrl } from './snippet.js';
import { readPageBlocks } from './page-reader.js';

/* The audience, as the two requests below carry it — nothing when the page declared none, so a
 * contractor widget's beacons look exactly as they did before the parameter existed. */
const audienceQuery = audience ? '&audience=' + encodeURIComponent(audience) : '';

/**
 * The text of the page the visitor is reading, capped and stripped of the furniture every page of a site
 * repeats — the walk itself lives in page-reader.js, shared with the outline the chat reads on demand.
 * This is the whole reason the report is a POST. Written from the URL alone, the question a page got was
 * about whatever its slug sounded like: a directory listing app agencies asked visitors whether they
 * would like to hear about mobile app development.
 * @returns {string} The extract, or '' when the page yields nothing usable
 */
function readPageText() {
    try {
        const page = readPageBlocks();
        const parts = [];
        const seen = {};
        let length = 0;

        function push(value) {
            // Deduplicated because a rendered page repeats itself: the h1 is often the title again, and
            // a card grid states its labels twice for screen readers.
            if (value.length < 2 || seen[value] || length >= pageTextMaxLength) {
                return;
            }

            seen[value] = true;
            parts.push(value);
            length += value.length + 1;
        }

        push(page.title);
        push(page.description);

        for (const block of page.blocks) {
            if (length >= pageTextMaxLength) {
                break;
            }
            push(block.text);
        }

        return parts.join('\n').slice(0, pageTextMaxLength);
    } catch (error) {
        /* A widget must never break the page it is a guest on, least of all while reading it. */
        return '';
    }
}

/**
 * Tells the server a visitor is on the current page — and what that page says — so automatic coverage
 * can write its question. The one signal that arrives without consent and without an identifier, on a
 * real page load and on a client-side route change alike.
 * Fire and forget in every direction: nothing on the page waits for it, a failure means the next visitor
 * asks again, and the caller only calls it for a page that has no question yet on a widget that covers
 * pages at all (see requestPageCoverageIfMissing) — which is what keeps this from becoming a second
 * request on every page load of every website.
 */
export function requestPageCoverage() {
    const currentPath = buildPageKey(runtime.pageQueryKeys);

    if (typeof fetch !== 'function') {
        endPageWait(currentPath);
        return;
    }

    try {
        const text = readPageText();

        // A page that yields no text yields no question either: the server would have nothing to write
        // from, and a guess off the URL is the very thing this body exists to replace. Nothing is
        // coming for this page, so the corner stops waiting and its generic bubble is released.
        if (!text) {
            endPageWait(currentPath);
            return;
        }

        // The host travels with the path: the server files the question under the page it belongs to,
        // and a path without its domain would land on whichever of a provider's sites came first.
        // text/plain deliberately: it is the only content type a cross-origin POST may carry without a
        // CORS preflight, so the report stays one request the way it was as a GET.
        const host = (location.host || '').toLowerCase();
        const path = currentPath;

        fetch(pageSeenUrl + '?host=' + encodeURIComponent(host) + '&page=' + encodeURIComponent(path)
            + audienceQuery, {
            method: 'POST',
            headers: {'Content-Type': 'text/plain;charset=UTF-8'},
            body: text,
            credentials: 'omit',
            keepalive: true
        }).then(function () {
            pollPageMessage(host, path, 0);
        }).catch(function () {
            /* A page must never break over a request it ignores anyway — but the corner must not keep
               waiting for a report that never arrived. */
            endPageWait(path);
        });
    } catch (error) {
        /* Blocked fetch (CSP, extensions): the same non-event as a failed one. */
        endPageWait(currentPath);
    }
}

/**
 * Asks how far this page's question is until it is there, until the server says none is coming, or
 * until the visitor has waited long enough. The corner holds its generic bubble back for exactly this
 * long: the site-wide sentence is what a page gets when nothing could be written FOR it, and showing it
 * during the seconds a real question takes teaches visitors to wave away the very bubble that was about
 * to be worth reading.
 * A question that arrives joins the served list, so everything downstream — the bubble, the chat that
 * opens on it, the frame that keeps it in step — treats it exactly like one the config carried.
 * @param {string} host - The host the report was filed under
 * @param {string} path - The normalized path it was filed under
 * @param {number} attempt - How many times this page has been asked about already
 */
function pollPageMessage(host, path, attempt) {
    if (attempt >= pageCoveragePollAttempts || path !== buildPageKey(runtime.pageQueryKeys)) {
        // Long enough, or the visitor moved on — whichever, this page is no longer being waited for.
        endPageWait(path);
        return;
    }

    setTimeout(function () {
        const language = effectiveLanguage();

        fetch(pageMessageUrl
            + '?host=' + encodeURIComponent(host)
            + '&page=' + encodeURIComponent(path)
            + (language ? '&lang=' + encodeURIComponent(language) : '')
            + audienceQuery, {credentials: 'omit'})
            .then(function (response) {
                return response.ok ? response.json() : null;
            })
            .then(function (data) {
                if (data && data.state === 'ready' && addServedPageMessage(data, host, path)) {
                    endPageWait(path);
                    return;
                }

                if (!data || data.state !== 'pending') {
                    endPageWait(path); // No question is coming for this page.
                    return;
                }

                pollPageMessage(host, path, attempt + 1);
            })
            .catch(function () {
                endPageWait(path);
            });
    }, pageCoveragePollMs);
}

/**
 * Takes a freshly written question into the served list, validated exactly like one out of the config
 * (see resolvePageMessages) — a payload this loader does not understand must not reach the corner.
 * @param {object} data - The state response carrying id, text and the answer offered under it
 * @param {string} host - The host it belongs to
 * @param {string} path - The normalized path it belongs to
 * @returns {boolean} Whether an entry was added
 */
function addServedPageMessage(data, host, path) {
    if (typeof runtime.servePageMessage !== 'function' || typeof data.id !== 'string'
        || typeof data.text !== 'string') {
        return false;
    }

    const text = data.text.trim();

    if (!data.id || !text || text.length > nudgeMaxTextLength) {
        return false;
    }

    const reply = typeof data.reply === 'string' ? data.reply.trim() : '';

    runtime.servePageMessage({
        id: data.id,
        host: host,
        path: path,
        text: text,
        reply: reply.length > 0 && reply.length <= nudgeMaxTextLength ? reply : '',
        // Filed under the audience it was asked for: the server answered the poll that carried it.
        audience: audience
    });

    return true;
}

/* The automatic questions already reported as shown in this document: once each is enough, the server
 * keeps one stamp a day. */
const shownReported = {};

/**
 * Tells the server an automatic question was shown, so its row counts as read and is not dropped as the
 * question of a deleted page. Its own request rather than the analytics beacon: that one is dropped when
 * a provider switches analytics off, and the retention must not depend on it. Only automatic questions
 * ("a:" ids) have a retention; a curated page's question is the provider's own.
 * @param {string} id - The id the question was served under
 */
export function reportPageMessageShown(id) {
    if (typeof id !== 'string' || id.indexOf('a:') !== 0 || shownReported[id] || typeof fetch !== 'function') {
        return;
    }

    shownReported[id] = true;

    try {
        fetch(pageMessageShownUrl + '?id=' + encodeURIComponent(id), {
            method: 'POST',
            credentials: 'omit',
            keepalive: true
        }).catch(function () { /* A lost report costs a stamp, never the page. */ });
    } catch (error) {
        /* Blocked fetch (CSP, extensions): nothing to do. */
    }
}

/** Ends the wait for one page, unless the corner has moved on to another one in the meantime. */
export function endPageWait(path) {
    if (runtime.waitingForPath === path) {
        runtime.waitingForPath = null;
    }
}
